/**
 * Role-based color utilities for DEMS UI
 * Provides consistent theming across all 5 user roles
 */

export type UserRole =
  | 'LawEnforcement'
  | 'Attorney'
  | 'PublicSubmitter'
  | 'CaseManager'
  | 'SystemAdmin'
  | 'Judge';

export interface RoleColorSet {
  /** Badge background + text (e.g., "bg-blue-100 text-blue-800") */
  badge: string;
  /** Light background for containers */
  bg: string;
  /** Background hover state */
  bgHover: string;
  /** Border color */
  border: string;
  /** Primary text color */
  text: string;
  /** Icon color */
  icon: string;
  /** Focus ring color */
  ring: string;
  /** Gradient for headers (from-X to-Y) */
  gradient: string;
  /** Solid background for buttons/accents */
  solid: string;
  /** Solid hover state */
  solidHover: string;
}

/**
 * Complete color palette for each role
 */
export const roleColors: Record<UserRole, RoleColorSet> = {
  LawEnforcement: {
    badge: 'bg-blue-100 text-blue-800',
    bg: 'bg-blue-50',
    bgHover: 'hover:bg-blue-100',
    border: 'border-blue-200',
    text: 'text-blue-600',
    icon: 'text-blue-500',
    ring: 'ring-blue-500 focus:ring-blue-500',
    gradient: 'from-blue-600 to-blue-800',
    solid: 'bg-blue-600',
    solidHover: 'hover:bg-blue-700',
  },
  Attorney: {
    badge: 'bg-purple-100 text-purple-800',
    bg: 'bg-purple-50',
    bgHover: 'hover:bg-purple-100',
    border: 'border-purple-200',
    text: 'text-purple-600',
    icon: 'text-purple-500',
    ring: 'ring-purple-500 focus:ring-purple-500',
    gradient: 'from-purple-600 to-purple-800',
    solid: 'bg-purple-600',
    solidHover: 'hover:bg-purple-700',
  },
  PublicSubmitter: {
    badge: 'bg-green-100 text-green-800',
    bg: 'bg-green-50',
    bgHover: 'hover:bg-green-100',
    border: 'border-green-200',
    text: 'text-green-600',
    icon: 'text-green-500',
    ring: 'ring-green-500 focus:ring-green-500',
    gradient: 'from-green-600 to-green-800',
    solid: 'bg-green-600',
    solidHover: 'hover:bg-green-700',
  },
  CaseManager: {
    badge: 'bg-amber-100 text-amber-800',
    bg: 'bg-amber-50',
    bgHover: 'hover:bg-amber-100',
    border: 'border-amber-200',
    text: 'text-amber-600',
    icon: 'text-amber-500',
    ring: 'ring-amber-500 focus:ring-amber-500',
    gradient: 'from-amber-600 to-amber-800',
    solid: 'bg-amber-600',
    solidHover: 'hover:bg-amber-700',
  },
  SystemAdmin: {
    badge: 'bg-red-100 text-red-800',
    bg: 'bg-red-50',
    bgHover: 'hover:bg-red-100',
    border: 'border-red-200',
    text: 'text-red-600',
    icon: 'text-red-500',
    ring: 'ring-red-500 focus:ring-red-500',
    gradient: 'from-red-600 to-red-800',
    solid: 'bg-red-600',
    solidHover: 'hover:bg-red-700',
  },
  Judge: {
    badge: 'bg-slate-100 text-slate-800',
    bg: 'bg-slate-50',
    bgHover: 'hover:bg-slate-100',
    border: 'border-slate-200',
    text: 'text-slate-700',
    icon: 'text-slate-500',
    ring: 'ring-slate-500 focus:ring-slate-500',
    gradient: 'from-slate-700 to-slate-900',
    solid: 'bg-slate-700',
    solidHover: 'hover:bg-slate-800',
  },
};

/**
 * Role descriptions for UI display
 */
export const roleDescriptions: Record<UserRole, string> = {
  LawEnforcement: 'Evidence submission & case management',
  Attorney: 'Case review & legal documentation',
  PublicSubmitter: 'Public evidence submission',
  CaseManager: 'Case oversight & audit access',
  SystemAdmin: 'Full system administration',
  Judge: 'Courtroom oversight & case adjudication',
};

/**
 * Role display names (formatted for UI)
 */
export const roleDisplayNames: Record<UserRole, string> = {
  LawEnforcement: 'Law Enforcement',
  Attorney: 'Attorney',
  PublicSubmitter: 'Public Submitter',
  CaseManager: 'Case Manager',
  SystemAdmin: 'System Admin',
  Judge: 'Judge',
};

export function getRoleDisplayName(role: string, department?: string): string {
  if (role === 'Attorney' && /district attorney/i.test(department || '')) {
    return 'Prosecutor';
  }

  return roleDisplayNames[role as UserRole] ?? role;
}

/**
 * Get color set for a role, with fallback to LawEnforcement
 */
export function getRoleColors(role: string): RoleColorSet {
  return roleColors[role as UserRole] ?? roleColors.LawEnforcement;
}

/**
 * Get badge classes for a role
 */
export function getRoleBadgeClasses(role: string): string {
  return getRoleColors(role).badge;
}

/**
 * Get role description
 */
export function getRoleDescription(role: string): string {
  return roleDescriptions[role as UserRole] ?? 'System user';
}

/**
 * Check if a role string is a valid UserRole
 */
export function isValidRole(role: string): role is UserRole {
  return role in roleColors;
}

/**
 * Evidence type icons and colors
 */
export const evidenceTypeStyles: Record<string, { icon: string; color: string }> = {
  'Video Recording': { icon: 'Video', color: 'bg-purple-100 text-purple-600' },
  'Photograph': { icon: 'Image', color: 'bg-blue-100 text-blue-600' },
  'Document': { icon: 'FileText', color: 'bg-amber-100 text-amber-600' },
  'Audio Recording': { icon: 'Music', color: 'bg-pink-100 text-pink-600' },
  'Digital File': { icon: 'FileBox', color: 'bg-gray-100 text-gray-600' },
  'Physical Evidence Photo': { icon: 'Image', color: 'bg-green-100 text-green-600' },
  'Witness Statement': { icon: 'FileText', color: 'bg-cyan-100 text-cyan-600' },
  'Expert Report': { icon: 'FileText', color: 'bg-orange-100 text-orange-600' },
  'Other': { icon: 'File', color: 'bg-gray-100 text-gray-600' },
};

/**
 * Get evidence type icon color classes
 */
export function getEvidenceTypeColor(type: string): string {
  return evidenceTypeStyles[type]?.color ?? 'bg-gray-100 text-gray-600';
}

/**
 * Status badge variants
 */
export type StatusVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral';

export const statusStyles: Record<StatusVariant, string> = {
  success: 'bg-green-100 text-green-800 ring-green-600/20',
  warning: 'bg-amber-100 text-amber-800 ring-amber-600/20',
  error: 'bg-red-100 text-red-800 ring-red-600/20',
  info: 'bg-blue-100 text-blue-800 ring-blue-600/20',
  neutral: 'bg-gray-100 text-gray-800 ring-gray-600/20',
};

/**
 * Map common status strings to variants
 */
export const statusVariantMap: Record<string, StatusVariant> = {
  // Case statuses
  open: 'success',
  pending: 'warning',
  closed: 'neutral',
  archived: 'info',
  // Evidence statuses
  verified: 'success',
  failed: 'error',
  // General
  active: 'success',
  inactive: 'neutral',
  error: 'error',
};

/**
 * Get status badge classes
 */
export function getStatusClasses(status: string): string {
  const variant = statusVariantMap[status.toLowerCase()] ?? 'neutral';
  return statusStyles[variant];
}
